import React from 'react';

import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Box, Chip, Stack, TextField, Tooltip } from '@mui/material';

import {
  CLAIM_CAMPAIGNS_URL,
  SOCIETE_CLAIM_TIERS,
  SOCIETE_CLAIM_TIER_LABELS,
} from '~/utils/societePayloadBuilder/statusCampaign/constants';
import { IMAGE_UPLOADER_URL } from '~/utils/societePayloadBuilder/constants';
import { PAYLOAD_BUILDER_PLACEHOLDERS } from '~/utils/societePayloadBuilder/placeholders';
import { normalizeCampaignSlug } from '~/utils/societePayloadBuilder/statusCampaign/slug';

import {
  FieldLabel,
  FieldLabelWithLink,
} from '../FieldLabels';
import { SectionCard } from '../SectionCard';
import { type PayloadFormSectionProps, updatePayloadState } from './StatusCampaignState';

export default function StatusClaimForm({ state, onChange }: PayloadFormSectionProps) {
  const handleSlugBlur = () => {
    const normalized = normalizeCampaignSlug(state.slug);
    if (normalized !== state.slug) {
      updatePayloadState(state, onChange, { slug: normalized });
    }
  };

  const toggleTierGranted = (tier: string) => {
    const next = state.tiersGranted.includes(tier)
      ? state.tiersGranted.filter((value) => value !== tier)
      : [...state.tiersGranted, tier];
    updatePayloadState(state, onChange, { tiersGranted: next });
  };

  return (
    <>
      <SectionCard title="Hero" subtitle="Headline, description, and hero image on the claim page.">
        <TextField
          label="Title"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroTitleClaim}
          value={state.heroTitle}
          onChange={(event) => updatePayloadState(state, onChange, { heroTitle: event.target.value })}
          fullWidth
        />
        <TextField
          label="Subtitle"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroSubtitleClaim}
          value={state.heroSubtitle}
          onChange={(event) => updatePayloadState(state, onChange, { heroSubtitle: event.target.value })}
          fullWidth
        />
        <Box>
          <FieldLabelWithLink label="Image ID" required href={IMAGE_UPLOADER_URL} />
          <TextField
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroImageId}
            value={state.heroImageId}
            onChange={(event) => updatePayloadState(state, onChange, { heroImageId: event.target.value })}
            fullWidth
            size="small"
          />
        </Box>
      </SectionCard>
      <SectionCard title="Campaign" subtitle="Campaign ID, URL slug, and terms reference.">
        <Box>
          <FieldLabelWithLink label="Campaign ID" required href={CLAIM_CAMPAIGNS_URL} />
          <TextField
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.campaignId}
            value={state.campaignId}
            onChange={(event) => updatePayloadState(state, onChange, { campaignId: event.target.value })}
            fullWidth
            size="small"
          />
        </Box>
        <TextField
          label="URL Slug"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.urlSlug}
          helperText="Lowercase letters, numbers, and hyphens only. Paste a full /societe/… URL to extract the slug."
          value={state.slug}
          onChange={(event) => updatePayloadState(state, onChange, { slug: event.target.value })}
          onBlur={handleSlugBlur}
          fullWidth
        />
        <TextField
          label="Terms Slug"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.termsSlugClaim}
          value={state.termsSlug}
          onChange={(event) => updatePayloadState(state, onChange, { termsSlug: event.target.value })}
          fullWidth
        />
      </SectionCard>
      <SectionCard title="Partner" subtitle="Partner name and logo shown on the claim experience.">
        <TextField
          label="Partner Name"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.partnerName}
          value={state.partnerName}
          onChange={(event) => updatePayloadState(state, onChange, { partnerName: event.target.value })}
          fullWidth
        />
        <Box>
          <FieldLabelWithLink label="Logo Image ID" required href={IMAGE_UPLOADER_URL} />
          <TextField
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.partnerLogoImageId}
            value={state.partnerLogoImageId}
            onChange={(event) => updatePayloadState(state, onChange, { partnerLogoImageId: event.target.value })}
            fullWidth
            size="small"
          />
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField label="Member Status" value="member" fullWidth disabled />
          <Tooltip title="Status Claim campaigns always use member status.">
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Stack>
      </SectionCard>
      <SectionCard title="Tiers granted" subtitle="Société tiers granted to approved claimants.">
        <FieldLabel required>Select at least one tier</FieldLabel>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {SOCIETE_CLAIM_TIERS.map((tier) => {
            const selected = state.tiersGranted.includes(tier);
            return (
              <Chip
                key={tier}
                label={SOCIETE_CLAIM_TIER_LABELS[tier]}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => toggleTierGranted(tier)}
                sx={{ minWidth: 96, justifyContent: 'center' }}
              />
            );
          })}
        </Stack>
      </SectionCard>
      <SectionCard title="Secondary section" subtitle="Supporting title and subtitle below the hero.">
        <TextField
          label="Title"
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.secondaryTitle}
          value={state.secondaryTitle}
          onChange={(event) => updatePayloadState(state, onChange, { secondaryTitle: event.target.value })}
          fullWidth
        />
        <TextField
          label="Subtitle"
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.secondarySubtitle}
          multiline
          minRows={3}
          value={state.secondarySubtitle}
          onChange={(event) => updatePayloadState(state, onChange, { secondarySubtitle: event.target.value })}
          fullWidth
        />
      </SectionCard>
    </>
  );
}
